import React, { useEffect, useState } from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { GET_EMAIL_TEMPLATES } from './graphql';

interface EmailTemplate {
  emailTemplateId: string;
  accountId: string;
  name: string;
  htmlContent: string;
  tagValueMapping: any;
  active: boolean;
  createdDate: string;
  modifiedDate: string;
}

interface EmailActionFormProps {
  accountId: string;
  initialData?: any;
  onChange: (data: any) => void;
}

const EmailActionForm: React.FC<EmailActionFormProps> = ({ accountId, initialData, onChange }) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialData?.templateId || '');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [ccTriggeredUser, setCcTriggeredUser] = useState<boolean>(initialData?.ccTriggeredUser ?? true);
  const [additionalRecipients, setAdditionalRecipients] = useState<string>(initialData?.additionalRecipients || '');

  const { data, loading, error } = useQuery(GET_EMAIL_TEMPLATES, {
    variables: { accountId },
    skip: !accountId,
  });

  const templates: EmailTemplate[] = data?.emailTemplates || [];

  useEffect(() => {
    if (selectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t.emailTemplateId === selectedTemplateId);
      setSelectedTemplate(template || null);
    }
  }, [selectedTemplateId, templates]);

  useEffect(() => {
    onChange({
      templateId: selectedTemplateId,
      templateName: selectedTemplate?.name || '',
      ccTriggeredUser,
      additionalRecipients,
    });
  }, [selectedTemplateId, selectedTemplate, ccTriggeredUser, additionalRecipients]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 200 }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading email templates: {error.message}</Alert>;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="email-template-select-label">Email Template</InputLabel>
          <Select
            labelId="email-template-select-label"
            value={selectedTemplateId}
            onChange={e => setSelectedTemplateId(e.target.value as string)}
            required
          >
            {templates
              .filter(t => t.active)
              .map(template => (
                <MenuItem key={template.emailTemplateId} value={template.emailTemplateId}>
                  {template.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>

      {selectedTemplate && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Template Preview
            </Typography>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Template Name: <strong>{selectedTemplate.name}</strong>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <div
              style={{
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                padding: '16px',
                backgroundColor: '#fff',
                maxHeight: '400px',
                overflowY: 'auto',
              }}
            >
              {selectedTemplate.htmlContent ? (
                <div
                  dangerouslySetInnerHTML={{ __html: selectedTemplate.htmlContent }}
                  style={{
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: '1.5',
                  }}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No content available
                </Typography>
              )}
            </div>
          </Grid>

          {selectedTemplate.tagValueMapping && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Available Tags: {Object.keys(selectedTemplate.tagValueMapping).join(', ')}
              </Typography>
            </Grid>
          )}
        </>
      )}

      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox checked={ccTriggeredUser} onChange={e => setCcTriggeredUser(e.target.checked)} />}
          label="CC user who triggered the workflow"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Additional Recipients (comma-separated emails)"
          value={additionalRecipients}
          onChange={e => setAdditionalRecipients(e.target.value)}
          fullWidth
          placeholder="email1@example.com, email2@example.com"
          helperText="Optional: Add additional email addresses to CC"
        />
      </Grid>
    </Grid>
  );
};

export default EmailActionForm;
